/**
 * SwipeableDispatchCard — extracted from DriverView.js
 *
 * Mobile-first dispatch card with horizontal-swipe gestures:
 *   • Swipe right  → Accept / Start / Complete (depending on dispatch.status)
 *   • Swipe left   → Decline / POD
 *
 * Pure presentational: parent passes the dispatch + per-action callbacks.
 */
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Package, MapPin, Clock, Navigation, CheckCircle2, ClipboardCheck,
  Loader2, X, ChevronLeft, ChevronRight,
} from 'lucide-react';

const SWIPE_THRESHOLD = 80;

export const SwipeableDispatchCard = ({
  dispatch,
  onAccept,
  onDecline,
  onStart,
  onComplete,
  onPOD,
  actionLoading,
  getStatusColor,
}) => {
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const startXRef = useRef(0);
  const cardRef = useRef(null);

  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
    setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startXRef.current;
    setSwipeX(Math.max(-120, Math.min(120, diff)));
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;

    if (swipeX > SWIPE_THRESHOLD) {
      if (dispatch.status === 'WAITING FOR CONFIRMATION') onAccept(dispatch.id);
      else if (dispatch.status === 'ACCEPTED') onStart(dispatch.id);
      else if (dispatch.status === 'STARTED') onComplete(dispatch.id);
    } else if (swipeX < -SWIPE_THRESHOLD) {
      if (dispatch.status === 'WAITING FOR CONFIRMATION') onDecline(dispatch.id);
      else if (dispatch.status === 'STARTED') onPOD(dispatch);
    }

    setSwipeX(0);
    setIsSwiping(false);
  };

  const getSwipeHint = () => {
    if (dispatch.status === 'WAITING FOR CONFIRMATION') {
      return { left: 'Decline', right: 'Accept', leftColor: 'bg-red-600', rightColor: 'bg-green-600' };
    }
    if (dispatch.status === 'ACCEPTED') {
      return { left: null, right: 'Start', leftColor: null, rightColor: 'bg-blue-600' };
    }
    if (dispatch.status === 'STARTED') {
      return { left: 'POD', right: 'Complete', leftColor: 'bg-purple-600', rightColor: 'bg-green-600' };
    }
    return { left: null, right: null };
  };

  const hints = getSwipeHint();
  const showLeftHint = swipeX < -30 && hints.left;
  const showRightHint = swipeX > 30 && hints.right;

  return (
    <div className="relative overflow-hidden rounded-xl" data-testid={`swipeable-dispatch-${dispatch.id}`}>
      {/* Swipe action backgrounds */}
      <div className="absolute inset-0 flex">
        <div className={`flex-1 ${hints.leftColor || 'bg-red-600'} flex items-center justify-start pl-4`}>
          {showLeftHint && (
            <div className="flex items-center gap-2 text-white font-semibold animate-pulse">
              <ChevronLeft className="h-5 w-5" />
              {hints.left}
            </div>
          )}
        </div>
        <div className={`flex-1 ${hints.rightColor || 'bg-green-600'} flex items-center justify-end pr-4`}>
          {showRightHint && (
            <div className="flex items-center gap-2 text-white font-semibold animate-pulse">
              {hints.right}
              <ChevronRight className="h-5 w-5" />
            </div>
          )}
        </div>
      </div>

      {/* Card content */}
      <div
        ref={cardRef}
        className="relative bg-slate-900 border border-slate-800 rounded-xl transition-transform"
        style={{ transform: `translateX(${swipeX}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-400" />
              <span className="text-white font-mono font-bold text-lg">
                {dispatch.container_number || 'N/A'}
              </span>
            </div>
            <Badge className={`${getStatusColor(dispatch.status)} text-xs px-2 py-1`}>
              {dispatch.status?.replace(/_/g, ' ')}
            </Badge>
          </div>

          {/* Location */}
          <div className="bg-slate-800/50 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <MapPin className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-white text-base font-medium">
                  {dispatch.destination || dispatch.address || 'No destination'}
                </p>
                {dispatch.dispatch_time_from && (
                  <p className="text-slate-400 text-sm mt-1 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {dispatch.dispatch_time_from} - {dispatch.dispatch_time_to || 'N/A'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Task description */}
          {dispatch.task && (
            <p className="text-slate-400 text-sm bg-slate-800 p-2 rounded-lg mb-3">
              {dispatch.task}
            </p>
          )}

          {/* Quick action buttons */}
          <div className="flex gap-2">
            {dispatch.status === 'WAITING FOR CONFIRMATION' && (
              <>
                <Button
                  size="lg"
                  className="flex-1 bg-green-600 hover:bg-green-500 h-12 text-base"
                  onClick={() => onAccept(dispatch.id)}
                  disabled={actionLoading === dispatch.id}
                  data-testid={`accept-btn-${dispatch.id}`}
                >
                  {actionLoading === dispatch.id ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Accept
                    </>
                  )}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1 border-red-500 text-red-400 hover:bg-red-500/10 h-12 text-base"
                  onClick={() => onDecline(dispatch.id)}
                  disabled={actionLoading === dispatch.id}
                  data-testid={`decline-btn-${dispatch.id}`}
                >
                  <X className="h-5 w-5 mr-2" />
                  Decline
                </Button>
              </>
            )}
            {dispatch.status === 'ACCEPTED' && (
              <Button
                size="lg"
                className="flex-1 bg-blue-600 hover:bg-blue-500 h-12 text-base"
                onClick={() => onStart(dispatch.id)}
                disabled={actionLoading === dispatch.id}
                data-testid={`start-btn-${dispatch.id}`}
              >
                {actionLoading === dispatch.id ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <>
                    <Navigation className="h-5 w-5 mr-2" />
                    Start Dispatch
                  </>
                )}
              </Button>
            )}
            {dispatch.status === 'STARTED' && (
              <>
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1 border-purple-500 text-purple-400 hover:bg-purple-500/10 h-12"
                  onClick={() => onPOD(dispatch)}
                  data-testid={`pod-btn-${dispatch.id}`}
                >
                  <ClipboardCheck className="h-5 w-5 mr-2" />
                  POD
                </Button>
                <Button
                  size="lg"
                  className="flex-1 bg-green-600 hover:bg-green-500 h-12 text-base"
                  onClick={() => onComplete(dispatch.id)}
                  disabled={actionLoading === dispatch.id}
                  data-testid={`complete-btn-${dispatch.id}`}
                >
                  {actionLoading === dispatch.id ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Complete
                    </>
                  )}
                </Button>
              </>
            )}
          </div>

          {/* Swipe hint */}
          {(hints.left || hints.right) && (
            <p className="text-center text-slate-500 text-xs mt-3">
              ← Swipe for quick actions →
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
